import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    PiArrowUpRightBold,
    PiArrowDownRightBold,
    PiShootingStarBold,
    PiTruckBold,
    PiAnchorBold,
    PiWarningBold,
    PiClockBold,
    PiCheckCircleBold,
    PiPackageBold,
    PiCurrencyDollarBold,
    PiChartBarBold,
    PiListBold,
    PiArrowRightBold,
} from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

/* ─── tipos ─────────────────────────────────────────────── */
type ExecutiveData = {
    kpis?: Record<string, number | null>;
    otif?: { on_time: number; late: number; total: number; percentage: number | null; avg_leadtime_days: number | null; ideal_days: number };
    by_stage?: Record<string, number>;
    open_tasks_by_area?: Record<string, number>;
    pending_suppliers?: Array<{ supplier: string; pending_total: number; pending_supplier: number }>;
    transit_time_by_carrier?: Array<{ shipping_line: string; avg_days: number; count: number }>;
    shipments_by_month?: { labels: string[]; opened: number[]; finalized: number[] };
    stock?: {
        total: number;
        by_category: Record<string, number>;
        by_supplier: Record<string, number>;
        depreciation: { total_value_brl: number; avg_days_stored: number; depreciated_value_brl: number; opportunity_cost_brl: number };
    };
};

/* ─── utils ──────────────────────────────────────────────── */
const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

/* ─── sub-componentes ────────────────────────────────────── */

/** KPI card horizontal no topo */
function KpiCard({
    label,
    value,
    icon: Icon,
    trend,
    trendLabel,
    to,
    alert,
}: {
    label: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    trend?: 'up' | 'down' | 'neutral';
    trendLabel?: string;
    to?: string;
    alert?: boolean;
}) {
    const content = (
        <div
            className={cn(
                'flex min-w-[11rem] flex-1 items-start gap-4 rounded-2xl border p-5 transition',
                alert
                    ? 'border-rose-200 bg-rose-50 dark:border-rose-800/60 dark:bg-rose-950/30'
                    : 'border-gray-200 bg-white dark:border-gray-700/60 dark:bg-gray-800/80',
                to && 'hover:shadow-md',
            )}
        >
            <span
                className={cn(
                    'flex size-12 shrink-0 items-center justify-center rounded-xl',
                    alert
                        ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                )}
            >
                <Icon className="size-6" />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p
                    className={cn(
                        'mt-1 text-3xl font-bold tabular-nums',
                        alert ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-gray-100',
                    )}
                >
                    {value}
                </p>
                {trendLabel && (
                    <p
                        className={cn(
                            'mt-1 flex items-center gap-0.5 text-xs font-medium',
                            trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : trend === 'down' ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500',
                        )}
                    >
                        {trend === 'up' && <PiArrowUpRightBold className="size-3.5" />}
                        {trend === 'down' && <PiArrowDownRightBold className="size-3.5" />}
                        {trendLabel}
                    </p>
                )}
            </div>
        </div>
    );

    return to ? <Link to={to} className="contents">{content}</Link> : content;
}

/** Card padrão */
function DashCard({
    title,
    subtitle,
    action,
    children,
    className,
}: {
    title: string;
    subtitle?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section
            className={cn(
                'flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-800/80',
                className,
            )}
        >
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700/40">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                    {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
                </div>
                {action}
            </div>
            <div className="flex-1 px-5 py-4">{children}</div>
        </section>
    );
}

/** SVG ring (donut) para OTIF */
function RingChart({ pct, color, size = 120 }: { pct: number; color: string; size?: number }) {
    const r = (size - 16) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={14} className="stroke-gray-100 dark:stroke-gray-700" />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                strokeWidth={14}
                stroke={color}
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeLinecap="round"
            />
        </svg>
    );
}

/** Barra horizontal */
function Bar({ label, value, max, sub, color = 'bg-blue-500' }: { label: string; value: number; max: number; sub?: string; color?: string }) {
    return (
        <li className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700 dark:text-gray-200 truncate max-w-[60%]">{label}</span>
                <span className="tabular-nums text-gray-500 dark:text-gray-400">
                    {value}{sub ? ` · ${sub}` : ''}
                </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className={cn('h-full rounded-full', color)} style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
            </div>
        </li>
    );
}

/** Linha de tabela */
function TableRow({ cols }: { cols: React.ReactNode[] }) {
    return (
        <tr className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 dark:border-gray-700/30 dark:hover:bg-gray-700/20">
            {cols.map((c, i) => (
                <td key={i} className={cn('px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300', i > 0 && 'text-right tabular-nums')}>
                    {c}
                </td>
            ))}
        </tr>
    );
}

/* ─── PÁGINA ─────────────────────────────────────────────── */
export default function ExecutiveDashboardPage() {
    const { data, isLoading } = useQuery<ExecutiveData>({
        queryKey: ['dashboard', 'executive'],
        queryFn: async () => (await api.get('/dashboard/executive')).data,
    });

    if (isLoading) return <LoadingState label="Carregando dashboard…" />;

    const kpis = data?.kpis ?? {};
    const otif = data?.otif;
    const byStage = data?.by_stage ?? {};
    const openTasks = data?.open_tasks_by_area ?? {};
    const pendingSuppliers = data?.pending_suppliers ?? [];
    const carriers = data?.transit_time_by_carrier ?? [];
    const months = data?.shipments_by_month;
    const stock = data?.stock;

    const maxStage = Math.max(1, ...Object.values(byStage).map(Number));
    const maxTask  = Math.max(1, ...Object.values(openTasks).map(Number));
    const maxSupp  = Math.max(1, ...pendingSuppliers.map((s) => s.pending_total));
    const maxCarr  = Math.max(1, ...carriers.map((c) => c.avg_days));

    const otifPct = otif?.percentage ?? 0;

    return (
        <div className="space-y-6">

            {/* ── Título ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard Geral</h1>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Visão executiva em tempo real</p>
                </div>
                <Link
                    to="/shipments"
                    className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                    Ver embarques <PiArrowRightBold className="size-4" />
                </Link>
            </div>

            {/* ── KPI strip ──────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-5">
                <KpiCard
                    label="Processos abertos"
                    value={kpis.open_shipments ?? '—'}
                    icon={PiAnchorBold}
                    trendLabel="em andamento"
                    to="/shipments"
                />
                <KpiCard
                    label="Em trânsito"
                    value={kpis.in_transit ?? '—'}
                    icon={PiTruckBold}
                    trendLabel="no mar"
                />
                <KpiCard
                    label="Financeiro pendente"
                    value={kpis.finance_pending ?? '—'}
                    icon={PiCurrencyDollarBold}
                    alert={(kpis.finance_pending ?? 0) > 0}
                    trendLabel={(kpis.finance_pending ?? 0) > 0 ? 'requer atenção' : 'sem pendências'}
                    trend={(kpis.finance_pending ?? 0) > 0 ? 'down' : 'up'}
                />
                <KpiCard
                    label="Atrasados"
                    value={kpis.overdue ?? '—'}
                    icon={PiClockBold}
                    alert={(kpis.overdue ?? 0) > 0}
                    trendLabel={(kpis.overdue ?? 0) > 0 ? 'fora do prazo ideal' : 'dentro do prazo'}
                    trend={(kpis.overdue ?? 0) > 0 ? 'down' : 'up'}
                />
                <KpiCard
                    label="Entregues no mês"
                    value={kpis.finished_month ?? '—'}
                    icon={PiCheckCircleBold}
                    trendLabel="finalizados"
                    trend="up"
                />
            </div>

            {/* ── KPI strip linha 2 ─────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
                <KpiCard
                    label="Aguard. alfândega"
                    value={kpis.awaiting_clearance ?? '—'}
                    icon={PiPackageBold}
                />
                <KpiCard
                    label="Risco alto"
                    value={kpis.high_risk ?? '—'}
                    icon={PiWarningBold}
                    alert={(kpis.high_risk ?? 0) > 0}
                    trendLabel={(kpis.high_risk ?? 0) > 0 ? 'monitorar' : 'sem risco'}
                    trend={(kpis.high_risk ?? 0) > 0 ? 'down' : 'up'}
                />
                <KpiCard
                    label="Containers no mar"
                    value={kpis.total_containers ?? '—'}
                    icon={PiAnchorBold}
                />
                <KpiCard
                    label="Lead time médio"
                    value={kpis.avg_leadtime_days ? `${kpis.avg_leadtime_days}d` : '—'}
                    icon={PiChartBarBold}
                    trendLabel={otif ? `ideal ${otif.ideal_days} dias` : undefined}
                />
                <KpiCard
                    label="Itens no CD"
                    value={kpis.stock_items ?? '—'}
                    icon={PiPackageBold}
                    trendLabel="em estoque"
                />
            </div>

            {/* ── Linha 1: OTIF + Tarefas + Estágios ───────────────── */}
            <div className="grid gap-5 lg:grid-cols-3">

                {/* OTIF */}
                <DashCard title="OTIF — On-Time In-Full" subtitle={otif ? `Lead time ideal: ${otif.ideal_days} dias` : undefined}>
                    {otif ? (
                        <div className="flex flex-col items-center gap-5">
                            <div className="relative flex items-center justify-center">
                                <RingChart pct={otifPct} color={otifPct >= 80 ? '#10b981' : otifPct >= 60 ? '#f59e0b' : '#ef4444'} size={140} />
                                <div className="absolute flex flex-col items-center">
                                    <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                        {otifPct !== null ? `${otifPct}%` : '—'}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">no prazo</span>
                                </div>
                            </div>
                            <div className="grid w-full grid-cols-3 divide-x divide-gray-100 text-center dark:divide-gray-700/50">
                                <div>
                                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{otif.on_time}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">No prazo</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{otif.late}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Atrasados</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                        {otif.avg_leadtime_days ? `${otif.avg_leadtime_days}d` : '—'}
                                    </p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Lead time</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="py-8 text-center text-sm text-gray-400">Sem dados de OTIF.</p>
                    )}
                </DashCard>

                {/* Tarefas por área */}
                <DashCard
                    title="Tarefas pendentes"
                    subtitle="por área"
                    action={
                        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            {Object.values(openTasks).reduce((s, v) => s + Number(v), 0)} total
                        </span>
                    }
                >
                    {Object.keys(openTasks).length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400">Sem tarefas pendentes 🎉</p>
                    ) : (
                        <ul className="space-y-3">
                            {Object.entries(openTasks)
                                .sort(([, a], [, b]) => Number(b) - Number(a))
                                .map(([label, value]) => (
                                    <Bar key={label} label={label} value={Number(value)} max={maxTask} color="bg-amber-500" />
                                ))}
                        </ul>
                    )}
                </DashCard>

                {/* Processos por estágio */}
                <DashCard
                    title="Pipeline de importação"
                    subtitle="processos por estágio"
                    action={
                        <Link to="/shipments" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                            Ver todos <PiArrowRightBold className="size-3" />
                        </Link>
                    }
                >
                    {Object.keys(byStage).length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400">Sem dados de estágio.</p>
                    ) : (
                        <ul className="space-y-3">
                            {Object.entries(byStage)
                                .sort(([, a], [, b]) => Number(b) - Number(a))
                                .map(([label, value]) => (
                                    <Bar key={label} label={label} value={Number(value)} max={maxStage} color="bg-blue-500" />
                                ))}
                        </ul>
                    )}
                </DashCard>
            </div>

            {/* ── Linha 2: Fornecedores + Armadores + Meses ─────────── */}
            <div className="grid gap-5 lg:grid-cols-3">

                {/* Pendências fornecedores */}
                <DashCard title="Pendências por fornecedor" subtitle="top por tarefas abertas">
                    {pendingSuppliers.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">Sem pendências ✓</p>
                    ) : (
                        <ul className="space-y-3">
                            {pendingSuppliers.map((r) => (
                                <Bar
                                    key={r.supplier}
                                    label={r.supplier}
                                    value={r.pending_total}
                                    max={maxSupp}
                                    sub={`forn.: ${r.pending_supplier}`}
                                    color="bg-rose-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>

                {/* Transit time armadores */}
                <DashCard title="Transit time médio" subtitle="dias por armador">
                    {carriers.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">Mínimo 3 embarques por armador.</p>
                    ) : (
                        <ul className="space-y-3">
                            {carriers.map((c) => (
                                <Bar
                                    key={c.shipping_line}
                                    label={c.shipping_line}
                                    value={c.avg_days}
                                    max={maxCarr}
                                    sub={`${c.count} embarques`}
                                    color="bg-violet-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>

                {/* Embarques por mês */}
                <DashCard title="Embarques" subtitle="últimos 6 meses">
                    {months ? (
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Mês</th>
                                    <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Abertos</th>
                                    <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Finaliz.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {months.labels.map((label, i) => (
                                    <TableRow
                                        key={label}
                                        cols={[
                                            <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>,
                                            months.opened[i],
                                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{months.finalized[i]}</span>,
                                        ]}
                                    />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="py-6 text-center text-sm text-gray-400">Sem dados mensais.</p>
                    )}
                </DashCard>
            </div>

            {/* ── Linha 3: Estoque ──────────────────────────────────── */}
            {stock && (
                <div className="grid gap-5 lg:grid-cols-3">
                    <DashCard title="Estoque por categoria">
                        <ul className="space-y-3">
                            {Object.entries(stock.by_category).map(([label, value]) => (
                                <Bar
                                    key={label}
                                    label={label}
                                    value={Number(value)}
                                    max={Math.max(1, ...Object.values(stock.by_category).map(Number))}
                                    color="bg-sky-500"
                                />
                            ))}
                        </ul>
                    </DashCard>

                    <DashCard title="Estoque por fornecedor">
                        <ul className="space-y-3">
                            {Object.entries(stock.by_supplier).map(([label, value]) => (
                                <Bar
                                    key={label}
                                    label={label}
                                    value={Number(value)}
                                    max={Math.max(1, ...Object.values(stock.by_supplier).map(Number))}
                                    color="bg-teal-500"
                                />
                            ))}
                        </ul>
                    </DashCard>

                    <DashCard
                        title="Depreciação de estoque"
                        subtitle={`${stock.total} itens no CD`}
                        action={
                            <PiListBold className="size-5 text-gray-400 dark:text-gray-500" />
                        }
                    >
                        <dl className="space-y-3 text-sm">
                            {[
                                { label: 'Valor total em estoque', value: brl(stock.depreciation.total_value_brl), cls: 'font-semibold text-gray-900 dark:text-gray-100' },
                                { label: 'Tempo médio parado',     value: `${stock.depreciation.avg_days_stored} dias`,         cls: 'text-gray-700 dark:text-gray-300' },
                                { label: 'Valor depreciado',       value: brl(stock.depreciation.depreciated_value_brl),        cls: 'font-semibold text-amber-600 dark:text-amber-400' },
                                { label: 'Custo de oportunidade',  value: brl(stock.depreciation.opportunity_cost_brl),         cls: 'font-semibold text-blue-600 dark:text-blue-400' },
                            ].map(({ label, value, cls }) => (
                                <div key={label} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-b-0 last:pb-0 dark:border-gray-700/40">
                                    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                                    <dd className={cls}>{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </DashCard>
                </div>
            )}

            {/* ── Ação rápida ───────────────────────────────────────── */}
            <div className="flex flex-wrap gap-3">
                {[
                    { label: 'Embarques', to: '/shipments', icon: PiAnchorBold },
                    { label: 'Calendário', to: '/calendar/containers', icon: PiShootingStarBold },
                    { label: 'Produtos', to: '/products', icon: PiPackageBold },
                    { label: 'COMEX', to: '/comex', icon: PiTruckBold },
                ].map(({ label, to, icon: Icon }) => (
                    <Link
                        key={to}
                        to={to}
                        className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        <Icon className="size-4" />
                        {label}
                    </Link>
                ))}
            </div>
        </div>
    );
}
